import React from 'react';
import { sanitizeStyleValue } from '../../Sanitizer.jsx';

export const toStyle = (style = {}) => {
  const allowed = [
    'background',
    'backgroundColor',
    'backgroundImage',
    'color',
    'fontFamily',
    'fontSize',
    'fontWeight',
    'lineHeight',
    'textAlign',
    'padding',
    'paddingTop',
    'paddingBottom',
    'paddingLeft',
    'paddingRight',
    'margin',
    'borderRadius',
    'border',
    'boxShadow',
    'minHeight',
    'maxWidth',
    'gap'
  ];

  const next = allowed.reduce((result, key) => {
    if (style[key] !== undefined && style[key] !== null) {
      result[key] = sanitizeStyleValue(style[key]);
    }
    return result;
  }, {});
  if (style.backgroundType === 'gradient' && style.backgroundGradient) {
    next.background = sanitizeStyleValue(style.backgroundGradient);
  }
  if (style.backgroundImage && style.backgroundType !== 'none') {
    const opacity = Math.round(Number(style.overlayOpacity || 0.45) * 255).toString(16).padStart(2, '0');
    const overlay = style.backgroundType === 'image' && style.overlayColor ? `linear-gradient(${sanitizeStyleValue(style.overlayColor)}${opacity}, ${sanitizeStyleValue(style.overlayColor)}${opacity}), ` : '';
    next.backgroundImage = `${overlay}url(${sanitizeStyleValue(style.backgroundImage)})`;
    next.backgroundSize = sanitizeStyleValue(style.backgroundSize || 'cover');
    next.backgroundPosition = sanitizeStyleValue(style.backgroundPosition || 'center');
    next.backgroundRepeat = sanitizeStyleValue(style.backgroundRepeat || 'no-repeat');
  }
  if (style.backgroundType === 'none') {
    delete next.background;
    delete next.backgroundColor;
    delete next.backgroundImage;
  }
  return applyFutureSectionStyle(next, { style });
};

const opacityHex = (opacity = 0.45) => Math.round(Number(opacity || 0) * 255).toString(16).padStart(2, '0');

export const colorWithOpacity = (color = '#000000', opacity = 0.45) => (
  String(color).startsWith('#')
    ? `${sanitizeStyleValue(color)}${opacityHex(opacity)}`
    : sanitizeStyleValue(color)
);

export const applyFutureSectionStyle = (base = {}, section = {}, theme = {}) => {
  const style = section?.style || {};
  const layout = section?.layout || {};
  const content = section?.content || {};
  const next = { ...base };
  const layers = [];
  const accent = style.glowColor || style.accentColor || theme.primary || '#008060';
  if (style.vignette) {
    layers.push(`radial-gradient(ellipse at center, transparent 45%, ${sanitizeStyleValue(style.vignetteColor || 'rgba(15,23,42,.34)')})`);
  }
  if (style.gridOverlay) {
    const gridColor = colorWithOpacity(style.gridColor || '#94a3b8', 0.22);
    layers.push(`linear-gradient(${gridColor} 1px, transparent 1px)`, `linear-gradient(90deg, ${gridColor} 1px, transparent 1px)`);
    next.backgroundSize = `${sanitizeStyleValue(style.gridSize || '32px')} ${sanitizeStyleValue(style.gridSize || '32px')}`;
  }
  if (style.noiseOverlay) {
    layers.push('repeating-linear-gradient(135deg, rgba(15,23,42,.035) 0 1px, transparent 1px 4px)');
  }
  if (layers.length) {
    next.backgroundImage = [...layers, next.backgroundImage].filter(Boolean).join(', ');
  }
  if (style.surfaceMode === 'glass') {
    const blur = sanitizeStyleValue(style.glassBlur || '18px');
    const saturate = sanitizeStyleValue(style.backdropSaturate || '1.25');
    next.backdropFilter = `blur(${blur}) saturate(${saturate})`;
    next.WebkitBackdropFilter = next.backdropFilter;
    if (!style.backgroundImage && style.backgroundType !== 'image') {
      next.backgroundColor = `rgba(255,255,255,${Number(style.glassOpacity || 0.74)})`;
    }
  }
  if (style.surfaceMode === 'neo') {
    next.boxShadow = next.boxShadow || '12px 12px 28px rgba(15,23,42,.12), -12px -12px 28px rgba(255,255,255,.75)';
  }
  if (style.surfaceMode === 'brutal') {
    next.border = next.border || '2px solid currentColor';
    next.boxShadow = next.boxShadow || '8px 8px 0 rgba(15,23,42,.22)';
  }
  if (style.surfaceMode === 'luminous' || style.borderGlow) {
    const glow = `0 0 ${sanitizeStyleValue(style.glowSpread || '42px')} ${colorWithOpacity(accent, style.glowStrength || 0.28)}`;
    next.boxShadow = [next.boxShadow, glow].filter(Boolean).join(', ');
  }
  if (style.backgroundBlendMode && style.backgroundBlendMode !== 'normal') next.backgroundBlendMode = sanitizeStyleValue(style.backgroundBlendMode);
  if (style.sectionOpacity) next.opacity = Number(style.sectionOpacity);
  if (style.scrollSnap) next.scrollSnapAlign = 'start';
  if (style.stickyTop) {
    next.position = 'sticky';
    next.top = sanitizeStyleValue(style.stickyTop);
    next.zIndex = 2;
  }
  if (style.animatedGradient) {
    next.backgroundSize = next.backgroundSize || '200% 200%';
  }
  if (style.revealMotion && style.revealMotion !== 'none') {
    const motionMap = {
      fade: 'templateRevealFade',
      slide: 'templateRevealSlide',
      scale: 'templateRevealScale',
      blur: 'templateRevealBlur'
    };
    const motion = motionMap[style.revealMotion] || 'templateRevealFade';
    next.animation = `${motion} ${sanitizeStyleValue(style.motionDuration || '700ms')} ${sanitizeStyleValue(style.motionEasing || 'ease-out')} ${sanitizeStyleValue(style.motionDelay || '0ms')} both`;
  }
  if (style.animatedGradient) {
    next.animation = [next.animation, 'templateGradientShift 14s ease infinite'].filter(Boolean).join(', ');
  }
  if (layout.contentDensity === 'dense' && !style.padding) next.padding = '48px 20px';
  if (layout.contentDensity === 'airy' && !style.padding) next.padding = '112px 24px';
  if (layout.verticalRhythm === 'tight' && !style.gap) next.gap = '16px';
  if (layout.verticalRhythm === 'loose' && !style.gap) next.gap = '40px';
  if (layout.edgeBleed) {
    next.width = '100vw';
    next.marginLeft = 'calc(50% - 50vw)';
    next.marginRight = 'calc(50% - 50vw)';
  }
  if (layout.safeArea) {
    next.paddingLeft = 'max(24px, env(safe-area-inset-left))';
    next.paddingRight = 'max(24px, env(safe-area-inset-right))';
  }
  if (content.readerMode === 'immersive') next.minHeight = next.minHeight || '80vh';
  if (content.readerMode === 'focused') {
    next.outline = next.outline || `1px solid ${colorWithOpacity(style.accentColor || theme.primary || '#008060', 0.22)}`;
    next.outlineOffset = '-1px';
  }
  if (content.readerMode === 'compact' && !style.padding) next.padding = '40px 20px';
  return next;
};

export const futureFrameProps = (section = {}) => {
  const style = section.style || {};
  const layout = section.layout || {};
  const content = section.content || {};
  return {
    'data-analytics-tag': content.analyticsTag || undefined,
    'data-future-hover-lift': style.hoverLift ? 'true' : undefined,
    'data-future-hover-glow': style.hoverGlow ? 'true' : undefined,
    'data-future-heading-gradient': style.headingGradient ? 'true' : undefined,
    'data-future-text-balance': style.textBalance ? 'true' : undefined,
    'data-future-highlight-marker': style.highlightMarker ? 'true' : undefined,
    'data-future-muted-opacity': style.mutedOpacity ? 'true' : undefined,
    'data-future-body-scale': style.bodyLeadScale && style.bodyLeadScale !== 'normal' ? style.bodyLeadScale : undefined,
    'data-future-number-style': style.numberStyle && style.numberStyle !== 'default' ? style.numberStyle : undefined,
    'data-future-callout-tone': style.calloutTone && style.calloutTone !== 'default' ? style.calloutTone : undefined,
    'data-future-parallax-depth': style.parallaxDepth ? 'true' : undefined,
    'data-future-tilt': style.tiltDepth ? 'true' : undefined,
    'data-future-card-density': layout.cardDensity || undefined,
    'data-future-card-equal-height': layout.cardEqualHeight ? 'true' : undefined,
    'data-future-section-divider': layout.sectionDivider && layout.sectionDivider !== 'none' ? layout.sectionDivider : undefined,
    style: {
      '--future-hover-scale': style.hoverScale || '1.015',
      '--future-glow-color': colorWithOpacity(style.glowColor || style.accentColor || '#008060', style.glowStrength || 0.3),
      '--future-glow-spread': style.glowSpread || '42px',
      '--future-heading-start': style.headingGradientStart || style.accentColor || '#008060',
      '--future-heading-end': style.headingGradientEnd || '#746EAC',
      '--future-heading-tracking': style.headingTracking || '0',
      '--future-muted-opacity': style.mutedOpacity || '0.78',
      '--future-divider-color': style.dividerColor || '#e5e7eb',
      '--future-tilt-depth': `${style.tiltDepth || 0}deg`,
      '--future-parallax-depth': `${style.parallaxDepth || 0}px`
    }
  };
};

export const getBlocks = (section, key = 'blocks') => {
  const blocks = section?.content?.[key];
  return Array.isArray(blocks) ? blocks.filter((item) => item?.visible !== false) : [];
};

export const getActions = (section) => {
  const actions = section?.content?.actions;
  return Array.isArray(actions) ? actions.filter((item) => item?.visible !== false) : [];
};

export const getImageUrl = (item) => (
  item?.imageContent?.displayLink
  || item?.imageContent?.storedLink
  || item?.imageUrl
  || item?.imageURL
  || ''
);

export const normalizeListResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.payload?.data)) return response.payload.data;
  if (Array.isArray(response?.content)) return response.content;
  return [];
};

export const ButtonLink = ({
  accentColor = '#3B9394',
  className,
  label,
  style,
  textColor = '#ffffff',
  url = '/'
}) => {
  if (!label) return null;

  return (
    <a
      className={className}
      href={url}
      style={{
        alignItems: 'center',
        background: accentColor,
        borderRadius: 8,
        color: textColor,
        display: 'inline-flex',
        fontWeight: 700,
        justifyContent: 'center',
        minHeight: 44,
        padding: '0 22px',
        textDecoration: 'none',
        ...style
      }}
    >
      {label}
    </a>
  );
};

